import React, { useEffect, useState } from 'react';

import { RiAddLine, RiInformationLine } from '@remixicon/react';

import { Badge } from '@/design-system/badge';
import { Banner } from '@/design-system/banner';
import { Button } from '@/design-system/button';
import { DatePicker } from '@/design-system/date-picker';
import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { driverBankOf, getDriverByIdV3 } from '@/services/api/drivers';
import { Input, InputRoot } from '@/design-system/input';
import { Label } from '@/design-system/label';
import { SearchSelect } from '@/design-system/search-select';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { Switch } from '@/design-system/switch';
import { Tag } from '@/design-system/tag';
import Direct4WRiderStep from '@/pages/shipments/components/steps/Direct4WRiderStep';
import type { UseDriversDropdownResult } from '@/pages/scheduled-instant/components/useDriversDropdown';
import type { UseProvidersDropdownResult } from '@/pages/shipments/components/useProvidersDropdown';
import {
  type UjpClientRule,
  type UjpEMoney,
  type UjpEstimateResult,
  type UjpPricedCostItem,
  type UjpSubconVendor,
  type UjpVehicle,
} from '@/services/api/ujp';
import type { RoutePlan } from '@/services/api/routePlans';
import {
  stopKindLabel,
} from '@/services/api/routeStopKind';
import type { TariffRing, TariffTipe } from '@/services/api/tariffs';
import RoutePlanDrawer from '@/pages/route-planner/components/RoutePlanDrawer';
import RoutePlanBuilder from '@/pages/route-planner/components/RoutePlanBuilder';
import { toPlanDraft, toRouteDraft } from './routePlanAdapter';

import { UJP_COPY } from '../copy';
import {
  formatDate,
  formatKm,
  formatRupiah,
  parseIsoDate,
  toIsoDate,
  toNum,
  todayIso,
} from '../format';
import CostItemsEditor, { type CostItemDraft } from './CostItemsEditor';
import KmInput from './KmInput';
import MoneyInput from './MoneyInput';
import { UjpTiles, usesQris } from './UjpTiles';
import {
  applyPlanCostsNow,
  applyRiderBank,
  applySavedRoute,
  applyVehicle,
  applyVendor,
  canApplyPlanCosts,
  detachRoutePlan,
  kmSummary,
  setPayee,
  type UjpErrors,
  type UjpFormState,
} from './ujpForm';
import { defaultKmProposed, routeStopBilling } from './ujpWizard';
import { useEnergyPriceRegions } from './useEnergyPriceRegions';
import type { UjpEstimateStatus } from './useUjpEstimate';

/**
 * The five step bodies of the UJP wizard (DD2: Info → Rute → Biaya → Driver →
 * Review, forward-only). Each one is a controlled view over the single
 * `UjpFormState`; `CreateUjpModal` owns the state, the estimate and the
 * navigation, so a step never fetches or decides anything by itself.
 */

/**
 * "Layanan" is hidden from the Info step for now (ops request, 12 Oct 2026).
 * The field stays on the form and the payload, so a UJP that already carries
 * a service type keeps it on edit; flip this to bring the picker back.
 */
const SHOW_SERVICE_TYPE = false;

const SERVICE_TYPES = ['Trucking 4W', 'Trucking 4W + Container'];
const SHIFTS = ['Dawn', 'Morning', 'Noon', 'Night'];
// Shift labels with their jam appended, e.g. "Subuh (00:00–06:00)", so ops
// picks by time-of-day. Base labels stay short for compact chips elsewhere.
const SHIFT_LABELS_WITH_HOURS: Record<string, string> = Object.fromEntries(
  SHIFTS.map((s) => [
    s,
    `${UJP_COPY.tariffs.shift[s as keyof typeof UJP_COPY.tariffs.shift]} (${
      UJP_COPY.tariffs.shiftHours[s as keyof typeof UJP_COPY.tariffs.shiftHours]
    })`,
  ])
);
const EMONEY_OPTIONS: UjpEMoney[] = ['NONE', 'FLAZZ', 'QRIS', 'FLAZZ_QRIS'];

export interface StepProps {
  form: UjpFormState;
  patch: (next: Partial<UjpFormState>) => void;
  errors: UjpErrors;
  clearError: (key: string) => void;
}

const sectionTitle =
  'text-[11px] font-bold uppercase tracking-[0.12em] text-text-soft-400';

const Section: React.FC<{
  title: React.ReactNode;
  action?: React.ReactNode;
  tint?: boolean;
  children: React.ReactNode;
}> = ({ title, action, tint, children }) => (
  <section
    className={`rounded-gsm border border-stroke-soft-200 ${tint ? 'bg-bg-weak-50' : ''}`}
  >
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stroke-soft-200 px-4 py-2.5">
      <h3 className={sectionTitle}>{title}</h3>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const Grid: React.FC<{ children: React.ReactNode; cols?: 2 | 3 }> = ({
  children,
  cols = 3,
}) => (
  <div
    className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${cols === 3 ? 'lg:grid-cols-3' : ''}`}
  >
    {children}
  </div>
);

/** A kit Select shaped like the rest of the form, without the Radix ceremony. */
const PlainSelect: React.FC<{
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  invalid?: boolean;
  /** Optional value→display map; the stored value stays `options[i]`. */
  labels?: Record<string, string>;
}> = ({ id, value, onChange, options, placeholder, invalid, labels }) => (
  <InputRoot size="md" invalid={invalid}>
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-full w-full cursor-pointer bg-transparent px-3 text-sm text-text-strong-950 outline-none"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {labels?.[o] ?? o}
        </option>
      ))}
    </select>
  </InputRoot>
);

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Info
// ─────────────────────────────────────────────────────────────────────────────

export interface InfoStepProps extends StepProps {
  clients: UseProvidersDropdownResult;
  rule: UjpClientRule | null;
  ruleLoading: boolean;
  /** The client's tariff mode (CR-4); the ring picker shows only for PER_RING. */
  tariffTipe?: TariffTipe | null;
  rings?: TariffRing[];
  ringsLoading?: boolean;
}

export const UjpInfoStep: React.FC<InfoStepProps> = ({
  form,
  patch,
  errors,
  clearError,
  clients,
  rule,
  ruleLoading,
  tariffTipe,
  rings = [],
  ringsLoading,
}) => {
  const c = UJP_COPY.info;
  const reverseCharge = toNum(rule?.reverseCharge);
  const regions = useEnergyPriceRegions();

  return (
    <Section title={UJP_COPY.wizard.steps[0]}>
      <Grid>
        <Field>
          <Label id="ujp-client-label" required>
            {c.client}
          </Label>
          <div role="group" aria-labelledby="ujp-client-label">
            <SearchSelect
              options={clients.options.map((p) => ({
                value: String(p.id),
                label: p.name,
              }))}
              value={form.clientId || undefined}
              selectedLabelFallback={
                form.clientId ? clients.labelFor(form.clientId) : undefined
              }
              onChange={(v) => {
                clearError('clientId');
                patch({ clientId: v ? String(v) : '' });
              }}
              placeholder={UJP_COPY.page.clientFilter}
              emptyMessage={clients.error ?? 'Client tidak ditemukan'}
              loading={clients.loading}
              hasMore={clients.hasMore}
              disableLocalFilter
              onSearchChange={clients.setSearch}
              onLoadMore={clients.loadMore}
              error={!!errors.clientId}
            />
          </div>
          {errors.clientId && <Hint tone="error">{errors.clientId}</Hint>}
          {form.clientId && !ruleLoading && rule && (
            <Hint>
              {rule.chargedPositioning
                ? UJP_COPY.route.chargedRule
                : UJP_COPY.route.unchargedRule}
              {reverseCharge > 0 ? ` · reverse ${formatRupiah(reverseCharge)}` : ''}
            </Hint>
          )}
        </Field>

        <Field>
          <Label htmlFor="ujp-delivery-date" required>
            {c.deliveryDate}
          </Label>
          <DatePicker
            value={parseIsoDate(form.deliveryDate)}
            onValueChange={(d) => {
              clearError('deliveryDate');
              patch({ deliveryDate: d ? toIsoDate(d) : '' });
            }}
            placeholder="Pilih tanggal…"
            size="md"
            startMonth={parseIsoDate(todayIso())}
            triggerClassName="rounded-lg"
          />
          {errors.deliveryDate && <Hint tone="error">{errors.deliveryDate}</Hint>}
        </Field>

        {SHOW_SERVICE_TYPE && (
          <Field>
            <Label htmlFor="ujp-service-type">{c.serviceType}</Label>
            <PlainSelect
              id="ujp-service-type"
              value={form.serviceType}
              onChange={(v) => patch({ serviceType: v })}
              options={SERVICE_TYPES}
              placeholder="Pilih layanan…"
            />
          </Field>
        )}

        <Field>
          <Label htmlFor="ujp-delivery-type">{c.deliveryType}</Label>
          <PlainSelect
            id="ujp-delivery-type"
            value={form.deliveryType}
            onChange={(v) => patch({ deliveryType: v as UjpFormState['deliveryType'] })}
            options={['SINGLE_DROP', 'MULTI_DROP']}
            labels={{ SINGLE_DROP: c.single, MULTI_DROP: c.multi }}
            placeholder="Pilih tipe…"
          />
        </Field>

        <Field>
          <Label htmlFor="ujp-shift">{c.shift}</Label>
          <PlainSelect
            id="ujp-shift"
            value={form.shift}
            onChange={(v) => patch({ shift: v })}
            options={SHIFTS}
            labels={SHIFT_LABELS_WITH_HOURS}
            placeholder="Pilih shift…"
          />
        </Field>

        <Field>
          <Label htmlFor="ujp-region">Daerah</Label>
          <PlainSelect
            id="ujp-region"
            value={form.region}
            onChange={(v) => patch({ region: v })}
            // The master's regions; a UJP being edited keeps a region the
            // master no longer has, so it still shows instead of blanking.
            options={
              form.region && !regions.regions.includes(form.region)
                ? [form.region, ...regions.regions]
                : regions.regions
            }
            placeholder={
              regions.loading ? 'Memuat daerah…' : 'Global (harga BBM tanpa daerah)'
            }
          />
          <Hint tone={regions.error ? 'error' : 'neutral'}>
            {regions.error
              ? 'Daftar daerah dari master BBM gagal dimuat; pakai harga global.'
              : 'Daerah dari master harga BBM. Harga daerah menang atas harga global.'}
          </Hint>
        </Field>

        <Field>
          <Label htmlFor="ujp-reverse">{c.reverse}</Label>
          <div className="flex h-10 items-center gap-2.5">
            <Switch
              id="ujp-reverse"
              checked={form.isReverse}
              onCheckedChange={(v) =>
                patch({ isReverse: v, reverseTouched: true })
              }
            />
            <span className="text-[13px] text-text-sub-600">
              {form.isReverse ? 'Ya, muatan balik' : 'Tidak'}
            </span>
          </div>
          <Hint>{reverseCharge > 0 ? c.reverseHint : c.reverseAuto}</Hint>
        </Field>

        {tariffTipe === 'PER_RING' && (
          <Field>
            <Label id="ujp-ring-label">{c.ring}</Label>
            <div role="group" aria-labelledby="ujp-ring-label">
              <SearchSelect
                options={rings.map((r) => ({ value: r.id, label: r.nama }))}
                value={form.ringId || undefined}
                onChange={(v) =>
                  patch({ ringId: v ? String(v) : '', ringTouched: true })
                }
                placeholder={c.ringPlaceholder}
                emptyMessage={c.ringEmpty}
                loading={!!ringsLoading}
              />
            </div>
            <Hint>{c.ringHint}</Hint>
          </Field>
        )}
      </Grid>
    </Section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — Rute
// ─────────────────────────────────────────────────────────────────────────────

export interface RouteStepProps extends StepProps {
  rule: UjpClientRule | null;
  /** Active route plans for the client (CR-2). */
  plans: RoutePlan[];
  plansLoading: boolean;
  plansError: string | null;
  clientName: string;
  onSearchPlans: (q: string) => void;
  /** A plan created via the drawer is added to the list by the parent. */
  onPlanCreated: (plan: RoutePlan) => void;
  /** Tests turn the server legs call off inside the drawer. */
  enableLegs?: boolean;
  /**
   * CR8-D3: the last server estimate, when there is one — its
   * `chargedPositioningOverride` wins over the local pre-estimate hint.
   */
  estimate?: UjpEstimateResult | null;
}

export const UjpRouteStep: React.FC<RouteStepProps> = ({
  form,
  patch,
  errors,
  clearError,
  rule,
  plans,
  plansLoading,
  plansError,
  clientName,
  onSearchPlans,
  onPlanCreated,
  enableLegs = true,
  estimate = null,
}) => {
  const c = UJP_COPY.route;
  const km = kmSummary(form, rule);
  const charged = rule?.chargedPositioning ?? null;
  const clientDefault = !!charged;
  // CR-8b: billing per stop from position + the edge override.
  const billing = routeStopBilling(form.route.stops, clientDefault);
  // Prefer the server flag; the local rule is only the pre-estimate hint.
  const override = estimate?.chargedPositioningOverride ?? km.override;
  const [drawerOpen, setDrawerOpen] = useState(false);
  // R3: build the route from the planner (pick a saved plan) or MANUALLY inline.
  // Defaults to the planner (the read-only summary shows the current route);
  // manual editing is opt-in via the toggle so it never hides the summary on edit.
  const [routeMode, setRouteMode] = useState<'planner' | 'manual'>('planner');
  const isManualRoute = routeMode === 'manual';

  // The default proposal follows the route until ops types their own km.
  useEffect(() => {
    if (form.kmTouched) return;
    const next = String(defaultKmProposed(form.route, !!charged) || '');
    if (next !== form.kmProposed) patch({ kmProposed: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.route, charged, form.kmTouched]);

  const selectedName =
    plans.find((p) => p.id === form.routePlanId)?.name ||
    (form.routePlanId && form.route.stops.length > 0
      ? `${form.route.stops[0]?.name ?? ''} → ${
          form.route.stops[form.route.stops.length - 1]?.name ?? ''
        }`
      : undefined);

  return (
    <div className="space-y-4">
      <Section title={UJP_COPY.wizard.steps[1]}>
        <div className="space-y-4">
          {charged != null && (
            <Banner
              status={charged ? 'success' : 'warning'}
              title={charged ? c.chargedRule : c.unchargedRule}
            >
              {c.ruleCaption}
            </Banner>
          )}

          <Field>
            <Label id="ujp-route-mode-label">Cara isi rute</Label>
            <SegmentedControl
              value={routeMode}
              onValueChange={(v: string) => {
                if (!v) return;
                setRouteMode(v as 'planner' | 'manual');
                // Leaving the planner detaches the saved-plan link: a manually
                // edited route must not keep tracing to a plan it no longer matches.
                if (v === 'manual') patch(detachRoutePlan());
                clearError('route');
              }}
              className="w-full [&>*]:flex-1"
            >
              <SegmentedItem value="planner">Dari Route Planner</SegmentedItem>
              <SegmentedItem value="manual">Manual</SegmentedItem>
            </SegmentedControl>
          </Field>

          {!isManualRoute && (
            <Field>
              <Label id="ujp-route-plan-label">{c.plan}</Label>
              <div
                className="flex flex-col gap-2 sm:flex-row sm:items-center"
                role="group"
                aria-labelledby="ujp-route-plan-label"
              >
                <div className="flex-1">
                  <SearchSelect
                    options={plans.map((p) => ({
                      value: p.id,
                      label: `${p.name} · ${formatKm(p.totalKm)}`,
                    }))}
                    value={form.routePlanId || undefined}
                    selectedLabelFallback={selectedName}
                    onChange={(v) => {
                      const plan = plans.find((p) => p.id === String(v));
                      if (plan) {
                        patch(applySavedRoute(form, plan, rule));
                        clearError('route');
                      }
                    }}
                    placeholder={c.planPlaceholder}
                    emptyMessage={
                      plansError ?? c.savedEmpty(clientName || 'client')
                    }
                    loading={plansLoading}
                    disableLocalFilter
                    onSearchChange={onSearchPlans}
                  />
                </div>
                <Button
                  tone="neutral"
                  style="stroke"
                  onClick={() => setDrawerOpen(true)}
                  disabled={!form.clientId}
                  className="shrink-0"
                >
                  <RiAddLine />
                  {c.newPlan}
                </Button>
              </div>
              {plansError ? (
                <Hint tone="error">{c.savedLoadFailed}</Hint>
              ) : (
                <Hint>{c.planCaption}</Hint>
              )}
              {form.planInfo?.notes && (
                <div
                  className="mt-2 rounded-gsm border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2"
                  data-testid="ujp-plan-notes"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                    {c.planNotes}
                  </p>
                  <p className="mt-1 whitespace-pre-line text-sm text-text-strong-950">
                    {form.planInfo.notes}
                  </p>
                </div>
              )}
            </Field>
          )}

          {isManualRoute && (
            <RoutePlanBuilder
              value={toPlanDraft(form.route)}
              onChange={(plan) => {
                // Manual edits never carry a saved-plan id (cleared on toggle too).
                patch({ route: toRouteDraft(plan), ...detachRoutePlan() });
                clearError('route');
              }}
              errors={errors}
              onClearError={clearError}
              clientId={form.clientId ? Number(form.clientId) : null}
              chargedPositioning={charged}
              enableLegs={enableLegs}
            />
          )}

          {!isManualRoute && form.route.stops.length > 0 && (
            <div className="rounded-xl border border-stroke-soft-200 bg-bg-weak-50 px-4 py-3">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
                {c.summary}
              </p>
              <div className="flex flex-wrap items-center gap-1.5">
                {form.route.stops.map((s, i) => (
                  <React.Fragment key={`${s.uid}`}>
                    {i > 0 && (
                      <span className="text-text-soft-400">→</span>
                    )}
                    <span className="inline-flex items-center gap-1 rounded-gsm border border-stroke-soft-200 bg-bg-white-0 px-2 py-0.5 text-[12px] tracking-tight text-text-strong-950">
                      <span className="text-[10px] font-semibold uppercase text-text-sub-600">
                        {stopKindLabel(s)}
                      </span>
                      {s.name || '—'}
                      {s.workflowName && (
                        <span
                          className="text-[10px] text-text-soft-400"
                          title={c.workflow}
                        >
                          · {s.workflowName}
                        </span>
                      )}
                      {/* CR-8b: an edge stop's EFFECTIVE billing (its own
                          override, else the client rule). Middle stops are
                          always billed and say nothing. */}
                      {billing[i]?.isEdge && (
                        <span
                          className={`text-[10px] ${
                            billing[i].override
                              ? 'font-semibold text-warning-dark'
                              : 'text-text-soft-400'
                          }`}
                          data-testid={`ujp-route-stop-${i + 1}-charged`}
                        >
                          ·{' '}
                          {billing[i].effectiveCharged
                            ? c.chargedYes.toLowerCase()
                            : c.chargedNo.toLowerCase()}
                        </span>
                      )}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              <Hint>{UJP_COPY.panel.allStopsShipped}</Hint>
            </div>
          )}
          {errors.route && <Hint tone="error">{errors.route}</Hint>}
        </div>
      </Section>

      <RoutePlanDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        clientId={form.clientId ? Number(form.clientId) : null}
        clientName={clientName}
        chargedPositioning={charged}
        onSaved={(plan) => {
          onPlanCreated(plan);
          patch(applySavedRoute(form, plan, rule));
          clearError('route');
        }}
        enableLegs={enableLegs}
      />

      <Section title="Jarak">
        <Grid>
          <Field>
            <Label>{c.kmAllLegs}</Label>
            <InputRoot size="md">
              <Input readOnly value={formatKm(km.all, false)} className="min-w-0 w-full text-right font-mono tabular-nums" />
            </InputRoot>
            <Hint>{c.kmAllLegsHint}</Hint>
          </Field>
          <Field>
            <Label>{c.kmCharged}</Label>
            <InputRoot size="md">
              <Input readOnly value={formatKm(km.charged, false)} className="min-w-0 w-full text-right font-mono tabular-nums" />
            </InputRoot>
            <Hint>
              {c.kmChargedHint}{' '}
              {km.anyPoolCharged ? UJP_COPY.panel.chargedRuleYes : UJP_COPY.panel.chargedRuleNo}.
            </Hint>
            {/* Under the hint, not in the label: a tag in the label pushes this
                input below its neighbours in the row. */}
            {override && (
              <Tag
                variant="stroke"
                size="xs"
                className="self-start text-warning-dark"
                data-testid="ujp-km-charged-override"
              >
                {c.chargedOverride}
              </Tag>
            )}
          </Field>
          <Field>
            <Label htmlFor="ujp-km-proposed" required>
              {c.kmProposed}
            </Label>
            <KmInput
              id="ujp-km-proposed"
              aria-label={c.kmProposed}
              value={form.kmProposed}
              onChange={(v) => {
                clearError('kmProposed');
                patch({ kmProposed: v, kmTouched: true });
              }}
              invalid={!!errors.kmProposed}
            />
            {errors.kmProposed ? (
              <Hint tone="error">{errors.kmProposed}</Hint>
            ) : (
              <Hint>{c.kmProposedHint}</Hint>
            )}
          </Field>
        </Grid>
      </Section>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Step 3 — Biaya
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The payment the last estimate priced this slot under. Zero slots are not
 * sent, so the estimate's row is found by the slot's name; a slot only gets a
 * chip while the estimate still describes it (same amount) — a typed change
 * hides it until the next estimate.
 */
const pricedRow = (
  estimate: UjpEstimateResult | null,
  row: CostItemDraft
): UjpPricedCostItem | null => {
  const priced = estimate?.costItems?.find((p) => p.label === row.label);
  if (!priced) return null;
  return Number(priced.amount) === Number(row.amount || 0) ? priced : null;
};

export interface CostStepProps extends StepProps {
  vehicles: UjpVehicle[];
  vehiclesLoading: boolean;
  onSearchVehicles: (q: string) => void;
  vendors: UjpSubconVendor[];
  vendorsLoading: boolean;
  onSearchVendors: (q: string) => void;
  /** The energy-price master row for the vehicle's fuel on the delivery date. */
  masterPrice: string | null;
  estimate: UjpEstimateResult | null;
  estimateStatus: UjpEstimateStatus;
  estimateError: string | null;
  onRetryEstimate: () => void;
}

export const UjpCostStep: React.FC<CostStepProps> = ({
  form,
  patch,
  errors,
  clearError,
  vehicles,
  vehiclesLoading,
  onSearchVehicles,
  vendors,
  vendorsLoading,
  onSearchVendors,
  masterPrice,
  estimate,
  estimateStatus,
  estimateError,
  onRetryEstimate,
}) => {
  const c = UJP_COPY.cost;
  const d = UJP_COPY.driver;
  const subcon = form.payee === 'subcon';
  const ev = form.energyType === 'EV';

  // Fill the price from the dated master until ops types over it (CR-D5).
  useEffect(() => {
    if (form.priceTouched || !masterPrice) return;
    const digits = String(Math.round(toNum(masterPrice)));
    if (digits !== form.energyPrice) patch({ energyPrice: digits });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterPrice, form.priceTouched]);

  const lockTag = (touched: boolean, fromMaster: boolean) =>
    touched ? (
      <Tag variant="stroke">{c.edited}</Tag>
    ) : fromMaster ? (
      <Tag variant="stroke">{c.fromMaster}</Tag>
    ) : null;

  return (
    <div className="space-y-4">
      <Section
        title={c.payee}
        action={
          <SegmentedControl
            value={form.payee}
            onValueChange={(v: string) => {
              if (v) patch(setPayee(form, v as UjpFormState['payee']));
            }}
            size="sm"
          >
            <SegmentedItem value="driver">{c.payeeDriver}</SegmentedItem>
            <SegmentedItem value="subcon">{c.payeeSubcon}</SegmentedItem>
          </SegmentedControl>
        }
      >
        <Grid>
          {subcon && (
            <Field>
              <Label id="ujp-vendor-label" required>
                {c.vendor}
              </Label>
              <div role="group" aria-labelledby="ujp-vendor-label">
                <SearchSelect
                  options={vendors.map((v) => ({
                    value: v.id,
                    label: v.city ? `${v.name} · ${v.city}` : v.name,
                  }))}
                  value={form.vendorId || undefined}
                  onChange={(v) => {
                    const vendor = vendors.find((x) => x.id === String(v));
                    if (vendor) {
                      clearError('vendorId');
                      patch(applyVendor(form, vendor));
                    }
                  }}
                  placeholder={c.vendorPlaceholder}
                  emptyMessage={c.vendorEmpty}
                  loading={vendorsLoading}
                  disableLocalFilter
                  onSearchChange={onSearchVendors}
                  error={!!errors.vendorId}
                />
              </div>
              {errors.vendorId && <Hint tone="error">{errors.vendorId}</Hint>}
            </Field>
          )}

          {subcon ? (
            // Subcon trucks are the vendor's, never in our vehicle master:
            // the plate is just typed (optional).
            <Field>
              <Label htmlFor="ujp-plate">{c.plate}</Label>
              <InputRoot size="md">
                <Input
                  id="ujp-plate"
                  value={form.plateNumber}
                  placeholder="B 1234 XYZ"
                  className="min-w-0 w-full font-mono"
                  maxLength={40}
                  onChange={(e) => patch({ plateNumber: e.target.value, vehicleId: '' })}
                />
              </InputRoot>
            </Field>
          ) : (
            <Field>
              <Label id="ujp-plate-label" required>
                {c.plate}
              </Label>
              <div role="group" aria-labelledby="ujp-plate-label">
                <SearchSelect
                  options={vehicles.map((v) => ({
                    value: v.id,
                    label: v.unitType
                      ? `${v.plateNumber} · ${v.unitType}`
                      : v.plateNumber,
                  }))}
                  value={form.vehicleId || undefined}
                  selectedLabelFallback={form.plateNumber || undefined}
                  onChange={(v) => {
                    const vehicle = vehicles.find((x) => x.id === String(v));
                    if (vehicle) {
                      clearError('plateNumber');
                      patch(applyVehicle(form, vehicle));
                    }
                  }}
                  placeholder={c.platePlaceholder}
                  emptyMessage={c.plateEmpty}
                  loading={vehiclesLoading}
                  disableLocalFilter
                  onSearchChange={(q) => {
                    onSearchVehicles(q);
                    // Typing a plate the master does not know still submits.
                    clearError('plateNumber');
                    patch({ plateNumber: q, vehicleId: '' });
                  }}
                  error={!!errors.plateNumber}
                />
              </div>
              {errors.plateNumber ? (
                <Hint tone="error">{errors.plateNumber}</Hint>
              ) : (
                <Hint>{c.plateManualHint}</Hint>
              )}
            </Field>
          )}

          <Field>
            <Label htmlFor="ujp-unit-type">{c.unitType}</Label>
            <InputRoot size="md">
              <Input
                className="min-w-0 w-full"
                id="ujp-unit-type"
                value={form.unitType}
                placeholder="Colt Diesel Engkel"
                onChange={(e) => patch({ unitType: e.target.value })}
              />
            </InputRoot>
          </Field>

          {subcon && (
            <Field>
              <Label htmlFor="ujp-nominal" required>
                {c.nominalTransfer}
              </Label>
              <MoneyInput
                id="ujp-nominal"
                aria-label={c.nominalTransfer}
                value={form.nominalTransfer}
                onChange={(v) => {
                  clearError('nominalTransfer');
                  patch({ nominalTransfer: v });
                }}
                invalid={!!errors.nominalTransfer}
              />
              {errors.nominalTransfer && (
                <Hint tone="error">{errors.nominalTransfer}</Hint>
              )}
            </Field>
          )}
        </Grid>

        {/* A subcon is paid one flat transfer: no e-money, no card. */}
        {!subcon && (
          <>
            <Field className="mt-4">
              <Label htmlFor="ujp-emoney" required>
                {c.eMoney}
              </Label>
              <SegmentedControl
                value={form.eMoney}
                onValueChange={(v: string) => {
                  if (v) patch({ eMoney: v as UjpEMoney });
                }}
                className="w-full [&>*]:flex-1"
              >
                {EMONEY_OPTIONS.map((k) => (
                  <SegmentedItem key={k} value={k}>
                    {c.eMoneyOptions[k]}
                  </SegmentedItem>
                ))}
              </SegmentedControl>
              <Hint>
                {`BBM → ${usesQris(form.eMoney) ? 'QRIS' : form.eMoney === 'FLAZZ' ? 'Flazz' : 'Transfer'} · toll & parkir tap → ${form.eMoney === 'FLAZZ' || form.eMoney === 'FLAZZ_QRIS' ? 'Flazz' : 'Transfer'} · lainnya → Transfer.`}
              </Hint>
            </Field>

            <Field className="mt-4">
              <Label htmlFor="ujp-emoney-card">{c.eMoneyCardNo}</Label>
              <InputRoot size="md">
                <Input
                  id="ujp-emoney-card"
                  value={form.eMoneyCardNo}
                  placeholder={c.eMoneyCardNoPlaceholder}
                  className="min-w-0 w-full font-mono"
                  maxLength={100}
                  onChange={(e) => patch({ eMoneyCardNo: e.target.value })}
                />
              </InputRoot>
              <Hint>{c.eMoneyCardNoHint}</Hint>
            </Field>
          </>
        )}
      </Section>

      {/*
        A subcon has no driver of ours: the server puts every subcon UJP on the
        placeholder "Driver Subcon" account, and ops closes its shipment with
        Mark as complete. Biaya only carries the VENDOR's rekening (prefilled
        by applyVendor).
      */}
      {subcon && (
        <>
          <Section title={d.bankSubcon}>
            <Grid>
              <Field>
                <Label htmlFor="ujp-subcon-bank-name" required>
                  {d.bankName}
                </Label>
                <InputRoot size="md" invalid={!!errors.bank}>
                  <Input
                    className="min-w-0 w-full"
                    id="ujp-subcon-bank-name"
                    value={form.bankName}
                    placeholder="BCA"
                    onChange={(e) => {
                      clearError('bank');
                      patch({ bankName: e.target.value });
                    }}
                  />
                </InputRoot>
              </Field>
              <Field>
                <Label htmlFor="ujp-subcon-bank-account" required>
                  {d.accountNumber}
                </Label>
                <InputRoot size="md" invalid={!!errors.bank}>
                  <Input
                    id="ujp-subcon-bank-account"
                    inputMode="numeric"
                    className="min-w-0 w-full font-mono tabular-nums"
                    value={form.accountNumber}
                    placeholder="1234567890"
                    onChange={(e) => {
                      clearError('bank');
                      patch({ accountNumber: e.target.value.replace(/\D/g, '') });
                    }}
                  />
                </InputRoot>
              </Field>
              <Field>
                <Label htmlFor="ujp-subcon-bank-holder" required>
                  {d.accountHolder}
                </Label>
                <InputRoot size="md" invalid={!!errors.bank}>
                  <Input
                    className="min-w-0 w-full"
                    id="ujp-subcon-bank-holder"
                    value={form.accountHolder}
                    placeholder="Nama pemilik rekening"
                    onChange={(e) => {
                      clearError('bank');
                      patch({ accountHolder: e.target.value });
                    }}
                  />
                </InputRoot>
              </Field>
            </Grid>
            {errors.bank && <Hint tone="error">{errors.bank}</Hint>}
          </Section>
        </>
      )}

      {/*
        DD9: for a subcon the cost block stays IN PLACE, disabled, keeping every
        typed value — toggling back to Driver must not lose the numbers.
      */}
      <Section
        title={subcon ? c.disabledForSubcon : 'Energi & jarak'}
        tint={subcon}
      >
        <Grid>
          {ev ? (
            <Field>
              <Label htmlFor="ujp-konsumsi" required>
                {c.konsumsi} {lockTag(form.baselineTouched, !!form.vehicleId)}
              </Label>
              <KmInput
                id="ujp-konsumsi"
                aria-label={c.konsumsi}
                value={form.konsumsiPerKm}
                onChange={(v) => {
                  clearError('konsumsiPerKm');
                  patch({ konsumsiPerKm: v, baselineTouched: true });
                }}
                disabled={subcon}
                locked={!form.baselineTouched}
                invalid={!!errors.konsumsiPerKm}
              />
              {errors.konsumsiPerKm && (
                <Hint tone="error">{errors.konsumsiPerKm}</Hint>
              )}
            </Field>
          ) : (
            <Field>
              <Label htmlFor="ujp-baseline" required>
                {c.baseline} {lockTag(form.baselineTouched, !!form.vehicleId)}
              </Label>
              <KmInput
                id="ujp-baseline"
                aria-label={c.baseline}
                value={form.baseline}
                onChange={(v) => {
                  clearError('baseline');
                  patch({ baseline: v, baselineTouched: true });
                }}
                disabled={subcon}
                locked={!form.baselineTouched}
                invalid={!!errors.baseline}
              />
              {errors.baseline && <Hint tone="error">{errors.baseline}</Hint>}
            </Field>
          )}

          <Field>
            <Label htmlFor="ujp-energy-price" required>
              {ev ? c.energyPriceEv : c.energyPriceFuel}{' '}
              {lockTag(form.priceTouched, !!masterPrice)}
            </Label>
            <MoneyInput
              id="ujp-energy-price"
              aria-label={ev ? c.energyPriceEv : c.energyPriceFuel}
              value={form.energyPrice}
              onChange={(v) => {
                clearError('energyPrice');
                patch({ energyPrice: v, priceTouched: true });
              }}
              disabled={subcon}
              locked={!form.priceTouched}
              invalid={!!errors.energyPrice}
            />
            {errors.energyPrice && <Hint tone="error">{errors.energyPrice}</Hint>}
          </Field>

          <Field>
            <Label htmlFor="ujp-km-echo">{UJP_COPY.route.kmProposed}</Label>
            <InputRoot size="md">
              <Input
                id="ujp-km-echo"
                readOnly
                value={formatKm(form.kmProposed, false)}
                className="min-w-0 w-full text-right font-mono tabular-nums"
              />
            </InputRoot>
          </Field>

          <Field>
            <Label htmlFor="ujp-override-bbm">{c.overrideBbm}</Label>
            <div className="flex h-10 items-center gap-2.5">
              <Switch
                id="ujp-override-bbm"
                checked={form.overrideBbm}
                disabled={subcon}
                onCheckedChange={(v) => patch({ overrideBbm: v, bbmTouched: true })}
              />
              <span className="text-[13px] text-text-sub-600">
                {form.overrideBbm ? 'Aktif' : 'Nonaktif'}
              </span>
            </div>
            <Hint>{c.overrideBbmHint}</Hint>
          </Field>

          {form.overrideBbm && (
            <Field>
              <Label htmlFor="ujp-bbm-fix" required>
                {c.bbmFix}
              </Label>
              <MoneyInput
                id="ujp-bbm-fix"
                aria-label={c.bbmFix}
                value={form.bbmFixOverride}
                onChange={(v) => patch({ bbmFixOverride: v, bbmTouched: true })}
                disabled={subcon}
              />
            </Field>
          )}
        </Grid>
      </Section>

      <Section title={c.operational} tint={subcon}>
        {!subcon && <Hint className="mb-3">{c.operationalHint}</Hint>}
        <CostItemsEditor
          value={form.costItems}
          onChange={(rows) => patch({ costItems: rows, costsTouched: true })}
          disabled={subcon}
          rowHint={(row) => {
            const priced = pricedRow(estimate, row);
            return priced ? (
              <span data-testid="ujp-cost-paid-by">{c.paidBy(priced.payment)}</span>
            ) : null;
          }}
        />
        {!subcon && form.costsFromPlan && form.planInfo && (
          <p className="mt-3 text-xs text-text-sub-600" data-testid="ujp-costs-from-plan">
            {c.fromPlan(
              form.planInfo.name,
              formatDate(form.planInfo.updatedAt),
              form.planInfo.updatedBy
            )}
          </p>
        )}
        {canApplyPlanCosts(form) && form.planInfo && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-text-sub-600">
            <span>{c.applyPlanHint(form.planInfo.name)}</span>
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              onClick={() => patch(applyPlanCostsNow(form))}
            >
              {c.applyPlan}
            </Button>
          </div>
        )}
        {!subcon && (
          <Field className="mt-4">
            <Label htmlFor="ujp-justifikasi">{c.justification}</Label>
            <InputRoot size="md">
              <Input
                className="min-w-0 w-full"
                id="ujp-justifikasi"
                value={form.justifikasi}
                maxLength={1000}
                placeholder={c.justificationPlaceholder}
                onChange={(e) => patch({ justifikasi: e.target.value })}
              />
            </InputRoot>
          </Field>
        )}
      </Section>

      <UjpBreakdown
        form={form}
        estimate={estimate}
        status={estimateStatus}
        error={estimateError}
        onRetry={onRetryEstimate}
      />
    </div>
  );
};

/** "Rincian estimasi" — the server's lines, never a browser-side formula (D5). */
export const UjpBreakdown: React.FC<{
  form: UjpFormState;
  estimate: UjpEstimateResult | null;
  status: UjpEstimateStatus;
  error: string | null;
  onRetry: () => void;
}> = ({ form, estimate, status, error, onRetry }) => {
  const c = UJP_COPY.cost;
  const e = UJP_COPY.estimate;
  const subcon = form.payee === 'subcon';
  const ev = form.energyType === 'EV';
  const qris = usesQris(form.eMoney);
  const flazz = form.eMoney === 'FLAZZ' || form.eMoney === 'FLAZZ_QRIS';
  const pending = status === 'pending' || status === 'stale';

  const statusChip =
    status === 'error' ? (
      <span className="flex items-center gap-1.5">
        <Badge status="error" size="sm">
          {e.failed}
        </Badge>
        <Button tone="neutral" style="ghost" size="xs" onClick={onRetry}>
          {e.retry}
        </Button>
      </span>
    ) : status === 'pending' ? (
      <Badge status="warning" size="sm">
        {e.pending}
      </Badge>
    ) : status === 'stale' ? (
      <Badge status="warning" size="sm">
        {e.stale}
      </Badge>
    ) : (
      <Badge status="neutral" size="sm">
        {e.server}
      </Badge>
    );

  return (
    <Section title={c.breakdown} action={statusChip} tint>
      {!estimate ? (
        <Hint tone={status === 'error' ? 'error' : 'neutral'}>
          {error ?? e.idle}
        </Hint>
      ) : (
        <div className={pending || status === 'error' ? 'opacity-60' : ''}>
          <div className="space-y-1.5 text-[13px]">
            {subcon ? (
              <Line k={UJP_COPY.panel.payeeSubcon} sub={UJP_COPY.panel.payeeSubconHint}>
                {formatRupiah(estimate.totalUangJalanTransfer)}
              </Line>
            ) : (
              <>
                <Line k={c.kmWithMargin(toNum(estimate.kmMarginPct))}>
                  {formatKm(estimate.totalKmWithMargin)}
                </Line>
                <Line k={c.liters}>
                  {`${formatKm(estimate.estimasiBbmLiter, false)} ${ev ? 'kWh' : 'L'}`}
                </Line>
                <Line k={c.bbmCost}>
                  {`${formatRupiah(estimate.totalBbmCost)} → ${qris ? e.qrisShort : flazz ? e.flazz : e.transfer}`}
                </Line>
                {(estimate.costItems ?? []).map((row, i) => (
                  <Line key={`${i}-${row.label}`} k={row.label}>
                    {`${formatRupiah(row.amount)} → ${row.payment === 'FLAZZ' ? e.flazz : e.transfer}`}
                  </Line>
                ))}
                {toNum(estimate.reverseChargeApplied) > 0 && (
                  <Line k={c.reverseLine}>
                    {`${formatRupiah(estimate.reverseChargeApplied)} → ${e.transfer}`}
                  </Line>
                )}
              </>
            )}
          </div>
          <UjpTiles
            className="mt-3"
            eMoney={form.eMoney}
            totals={{
              flazz: estimate.totalUangJalanFlazz,
              qris: estimate.totalUangJalanQris,
              transfer: estimate.totalUangJalanTransfer,
            }}
          />
        </div>
      )}
    </Section>
  );
};

const Line: React.FC<{
  k: React.ReactNode;
  sub?: React.ReactNode;
  children: React.ReactNode;
}> = ({ k, sub, children }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-text-sub-600">
      {k}
      {sub && <span className="block text-[11px] text-text-soft-400">{sub}</span>}
    </span>
    <span className="shrink-0 font-mono font-semibold tabular-nums text-text-strong-950">
      {children}
    </span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Step 4 — Driver
// ─────────────────────────────────────────────────────────────────────────────

export interface DriverStepProps extends StepProps {
  riders: UseDriversDropdownResult;
}

export const UjpDriverStep: React.FC<DriverStepProps> = ({
  form,
  patch,
  errors,
  clearError,
  riders,
}) => {
  const c = UJP_COPY.driver;
  const subcon = form.payee === 'subcon';
  const riderId = form.rider.id;
  const [bankPrefill, setBankPrefill] = useState<
    'idle' | 'filled' | 'empty' | 'failed'
  >('idle');

  // A picked driver prefills the rekening from the driver master
  // (GET /v3/drivers/:id carries driver_bank). Only an untouched bank block is
  // filled; the fields stay editable. A stale response (driver changed again)
  // is ignored.
  useEffect(() => {
    setBankPrefill('idle');
    if (subcon || !riderId) return;
    let cancelled = false;
    getDriverByIdV3(riderId)
      .then((driver) => {
        if (cancelled) return;
        const bank = driverBankOf(driver);
        if (!bank) {
          setBankPrefill('empty');
          return;
        }
        const next = applyRiderBank(form, bank);
        if (next === form) return;
        patch({
          bankName: next.bankName,
          accountNumber: next.accountNumber,
          accountHolder: next.accountHolder,
        });
        clearError('bank');
        setBankPrefill('filled');
      })
      .catch(() => {
        if (!cancelled) setBankPrefill('failed');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riderId, subcon]);

  return (
    <div className="space-y-4">
      <Section title={c.section}>
        {subcon ? (
          <Field>
            <Label htmlFor="ujp-vendor-driver">{c.vendorDriver}</Label>
            <InputRoot size="md">
              <Input
                className="min-w-0 w-full"
                id="ujp-vendor-driver"
                value={form.vendorDriverName}
                placeholder="mis. Pak Tono"
                onChange={(e) => patch({ vendorDriverName: e.target.value })}
              />
            </InputRoot>
            <Hint>{c.vendorDriverHint}</Hint>
          </Field>
        ) : (
          <Grid cols={2}>
            <Direct4WRiderStep
              value={form.rider}
              onChange={(p) => {
                clearError('rider');
                patch({ rider: { ...form.rider, ...p } });
              }}
              riders={riders}
              error={errors.rider}
              idPrefix="ujp"
            />
          </Grid>
        )}
      </Section>

      <Section title={subcon ? c.bankSubcon : c.bankDriver}>
        <Grid>
          <Field>
            <Label htmlFor="ujp-bank-name" required>
              {c.bankName}
            </Label>
            <InputRoot size="md" invalid={!!errors.bank}>
              <Input
                className="min-w-0 w-full"
                id="ujp-bank-name"
                value={form.bankName}
                placeholder="BCA"
                onChange={(e) => {
                  clearError('bank');
                  patch({ bankName: e.target.value });
                }}
              />
            </InputRoot>
          </Field>
          <Field>
            <Label htmlFor="ujp-bank-account" required>
              {c.accountNumber}
            </Label>
            <InputRoot size="md" invalid={!!errors.bank}>
              <Input
                id="ujp-bank-account"
                inputMode="numeric"
                className="min-w-0 w-full font-mono tabular-nums"
                value={form.accountNumber}
                placeholder="1234567890"
                onChange={(e) => {
                  clearError('bank');
                  patch({ accountNumber: e.target.value.replace(/\D/g, '') });
                }}
              />
            </InputRoot>
          </Field>
          <Field>
            <Label htmlFor="ujp-bank-holder" required>
              {c.accountHolder}
            </Label>
            <InputRoot size="md" invalid={!!errors.bank}>
              <Input
                className="min-w-0 w-full"
                id="ujp-bank-holder"
                value={form.accountHolder}
                placeholder="Nama pemilik rekening"
                onChange={(e) => {
                  clearError('bank');
                  patch({ accountHolder: e.target.value });
                }}
              />
            </InputRoot>
          </Field>
        </Grid>
        {errors.bank && <Hint tone="error">{errors.bank}</Hint>}
        {!errors.bank && !subcon && bankPrefill === 'filled' && (
          <Hint>{c.bankPrefilled}</Hint>
        )}
        {!errors.bank && !subcon && bankPrefill === 'empty' && (
          <Hint>{c.bankPrefillEmpty}</Hint>
        )}
        {!errors.bank && !subcon && bankPrefill === 'failed' && (
          <Hint>{c.bankPrefillFailed}</Hint>
        )}
      </Section>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Step 5 — Review
// ─────────────────────────────────────────────────────────────────────────────

export interface ReviewStepProps {
  form: UjpFormState;
  rule: UjpClientRule | null;
  clientName: string;
  estimate: UjpEstimateResult | null;
  estimateStatus: UjpEstimateStatus;
  estimateError: string | null;
  onRetryEstimate: () => void;
  onEdit: (step: 1 | 2 | 3 | 4) => void;
  missing: string[];
}

export const UjpReviewStep: React.FC<ReviewStepProps> = ({
  form,
  rule,
  clientName,
  estimate,
  estimateStatus,
  estimateError,
  onRetryEstimate,
  onEdit,
  missing,
}) => {
  const w = UJP_COPY.wizard;
  const km = kmSummary(form, rule);
  const clientDefault = !!rule?.chargedPositioning;
  const billing = routeStopBilling(form.route.stops, clientDefault);
  // CR-8: the server's verdict once it has estimated, the local rule before.
  const anyPoolCharged = estimate?.chargedPositioning ?? km.anyPoolCharged;
  const override = estimate?.chargedPositioningOverride ?? km.override;
  const subcon = form.payee === 'subcon';

  const completeness = [
    missing.length === 0 ? w.completenessOk : w.completenessMissing(missing),
    // CR8-D5: every stop ships; only the km changes with billing. Subcon now
    // creates a shipment too (via the shared driver), so both payees say so.
    UJP_COPY.panel.allStopsShipped,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="space-y-4">
      <ReviewSection title={w.steps[0]} onEdit={() => onEdit(1)}>
        <Line k={UJP_COPY.info.client}>{clientName || '—'}</Line>
        <Line k={UJP_COPY.info.deliveryDate}>
          {`${form.deliveryDate} ${form.shift}`.trim()}
        </Line>
        <Line k={UJP_COPY.info.deliveryType}>
          {form.deliveryType === 'MULTI_DROP'
            ? UJP_COPY.info.multi
            : UJP_COPY.info.single}
          {form.isReverse ? ` · ${UJP_COPY.page.reverse}` : ''}
        </Line>
      </ReviewSection>

      <ReviewSection title={w.steps[1]} onEdit={() => onEdit(2)}>
        {form.route.stops.map((s, i) => (
          <Line
            key={s.uid}
            k={`${i + 1}. ${s.name || '—'}`}
            sub={[
              stopKindLabel(s),
              s.workflowName ? `${UJP_COPY.route.workflow} ${s.workflowName}` : null,
              // CR-8b: an edge stop's effective billing.
              billing[i]?.isEdge
                ? billing[i].effectiveCharged
                  ? UJP_COPY.route.chargedYes.toLowerCase()
                  : UJP_COPY.route.chargedNo.toLowerCase()
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            {i > 0 ? formatKm(form.route.legs[i - 1]?.km) : '—'}
          </Line>
        ))}
        <Line
          k={
            <span className="inline-flex flex-wrap items-center gap-1.5">
              {`${UJP_COPY.route.kmAllLegs} / ${UJP_COPY.route.kmCharged}`}
              {override && (
                <Tag
                  variant="stroke"
                  size="xs"
                  className="text-warning-dark"
                  data-testid="ujp-review-charged-override"
                >
                  {UJP_COPY.route.chargedOverride}
                </Tag>
              )}
            </span>
          }
          sub={anyPoolCharged ? UJP_COPY.panel.chargedRuleYes : UJP_COPY.panel.chargedRuleNo}
        >
          {`${formatKm(km.all, false)} / ${formatKm(km.charged, false)} km`}
        </Line>
        <Line k={UJP_COPY.route.kmProposed}>{formatKm(form.kmProposed)}</Line>
      </ReviewSection>

      <ReviewSection title={w.steps[2]} onEdit={() => onEdit(3)}>
        <Line k={UJP_COPY.cost.payee}>
          {subcon ? UJP_COPY.cost.payeeSubcon : UJP_COPY.cost.payeeDriver} ·{' '}
          {form.plateNumber || '—'}
        </Line>
        {subcon ? (
          <>
            <Line k={UJP_COPY.cost.nominalTransfer}>
              {formatRupiah(form.nominalTransfer)}
            </Line>
            {/* No driver of ours: the server uses the placeholder account. */}
            <Line k={UJP_COPY.panel.driver}>{UJP_COPY.cost.subconDriver}</Line>
            <Line k={UJP_COPY.driver.bankSubcon}>
              {`${form.bankName} · ${form.accountNumber} · ${form.accountHolder}`}
            </Line>
          </>
        ) : (
          <>
            <Line k={UJP_COPY.cost.bbmCost}>
              {formatRupiah(estimate?.totalBbmCost)}
            </Line>
            <Line k={UJP_COPY.cost.eMoney}>
              {UJP_COPY.cost.eMoneyOptions[form.eMoney]}
            </Line>
            {form.eMoneyCardNo.trim() && (
              <Line k={UJP_COPY.cost.eMoneyCardNo}>
                {form.eMoneyCardNo.trim()}
              </Line>
            )}
          </>
        )}
        {estimate ? (
          <>
            <UjpTiles
              className="mt-3"
              eMoney={subcon ? 'NONE' : form.eMoney}
              totals={{
                flazz: estimate.totalUangJalanFlazz,
                qris: estimate.totalUangJalanQris,
                transfer: estimate.totalUangJalanTransfer,
              }}
            />
            <div className="mt-3 flex items-center justify-between border-t border-stroke-soft-200 pt-3">
              <span className="label-eyebrow">{UJP_COPY.panel.totalLabel}</span>
              <span
                className="font-mono text-[28px] font-extrabold tabular-nums text-text-strong-950"
                aria-live="polite"
              >
                {formatRupiah(estimate.estimatedAmount)}
              </span>
            </div>
          </>
        ) : (
          <Hint tone={estimateStatus === 'error' ? 'error' : 'neutral'}>
            {estimateError ?? UJP_COPY.estimate.idle}
          </Hint>
        )}
        {estimateStatus === 'error' && (
          <Hint tone="error">
            {UJP_COPY.estimate.failed}{' '}
            <Button tone="neutral" style="ghost" size="xs" onClick={onRetryEstimate}>
              {UJP_COPY.estimate.retry}
            </Button>
          </Hint>
        )}
        {(estimateStatus === 'pending' || estimateStatus === 'stale') && (
          <Hint>{UJP_COPY.estimate.pending}</Hint>
        )}
      </ReviewSection>

      {/* Subcon has no Driver step — its vendor rekening shows in Biaya. */}
      {!subcon && (
        <ReviewSection title={w.steps[3]} onEdit={() => onEdit(4)}>
          <Line k={UJP_COPY.panel.driver}>{form.rider.name || '—'}</Line>
          <Line k={UJP_COPY.driver.bankDriver}>
            {`${form.bankName} · ${form.accountNumber} · ${form.accountHolder}`}
          </Line>
        </ReviewSection>
      )}

      <p className="flex items-start gap-1.5 px-1 text-[12px] text-text-sub-600">
        <RiInformationLine className="size-4 shrink-0" aria-hidden />
        <span>
          <b>{w.completeness}:</b> {completeness} {w.pendingRefHint}.
        </span>
      </p>
    </div>
  );
};

const ReviewSection: React.FC<{
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}> = ({ title, onEdit, children }) => (
  <section className="border-b border-stroke-soft-200 pb-4 last:border-b-0">
    <div className="mb-2 flex items-center justify-between">
      <h3 className={sectionTitle}>{title}</h3>
      <Button tone="neutral" style="ghost" size="xs" onClick={onEdit}>
        {UJP_COPY.wizard.edit}
      </Button>
    </div>
    <div className="space-y-1.5 text-[13px]">{children}</div>
  </section>
);
